import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, RefreshCw, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STATUSES = ['Order Received', 'Being Prepared', 'Out for Delivery', 'Delivered'];
const STORAGE_KEY = 'farmfreshlily_admin_pw';

const getOrderTypeLabel = (orderType, quantity) => {
  if (orderType === 'stems') return `${quantity} ${quantity === 1 ? 'stem' : 'stems'}`;
  if (orderType === 'subscription-weekly') return 'Weekly Sub';
  if (orderType === 'subscription-monthly') return 'Monthly Sub';
  return `${quantity} stems`;
};

export const Admin = () => {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [productsMap, setProductsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [pwInput, setPwInput] = useState('');

  // Mount-only fetch of the product catalog for id→name lookups.
  // API is a module-scope const; setProductsMap is stable. Empty deps intentional.
  useEffect(() => {
    axios.get(`${API}/products`).then(res => {
      const map = {};
      res.data.forEach(p => { map[p.id] = p; });
      setProductsMap(map);
    }).catch(() => {});
  }, []);

  const loadOrders = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/orders`, { password: pw });
      setOrders(res.data.orders || []);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, pw);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Invalid password');
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
        setPassword('');
      } else {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (password) loadOrders(password);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    setPassword(pwInput);
    loadOrders(pwInput);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await axios.patch(`${API}/admin/orders/${orderId}/status`, { password, status: newStatus });
      setOrders(orders.map(o => o.id === orderId ? res.data : o));
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 flex items-center justify-center" data-testid="admin-login">
        <form onSubmit={handleLogin} className="bg-brand-blush/30 gold-border-glow p-8 rounded-sm max-w-md w-full">
          <div className="flex justify-center mb-6">
            <Lock className="w-10 h-10 text-brand-gold" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl text-brand-green-dark text-center mb-6">Admin Login</h1>
          <input
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Admin password"
            className="w-full px-4 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm focus:outline-none focus:border-brand-gold font-body text-brand-green-dark mb-4"
            data-testid="admin-password-input"
            required
          />
          <button
            type="submit"
            className="w-full bg-brand-green text-brand-cream py-3 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
            data-testid="admin-login-btn"
          >
            {loading ? 'Checking...' : 'Login'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-4 sm:px-6 fade-in" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-brand-green-dark">Orders Dashboard</h1>
            <p className="font-body text-sm text-brand-green-dark/60 mt-1">{orders.length} orders</p>
          </div>
          <button
            onClick={() => loadOrders(password)}
            className="flex items-center gap-2 bg-brand-green text-brand-cream px-4 py-2 rounded-sm font-body text-sm hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            Refresh
          </button>
        </div>

        {loading && <p className="text-brand-green-dark font-body">Loading...</p>}

        {!loading && orders.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-brand-gold/40 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-body text-brand-green-dark/60">No orders yet.</p>
          </div>
        )}

        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="bg-brand-cream gold-border-glow p-4 sm:p-6 rounded-sm" data-testid={`admin-order-${order.id}`}>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mb-4">
                <div>
                  <p className="font-heading text-lg text-brand-green-dark">#{order.id.substring(0, 8).toUpperCase()}</p>
                  <p className="font-body text-xs text-brand-green-dark/60">{new Date(order.createdAt).toLocaleString('en-IN')}</p>
                </div>
                <p className="font-heading text-xl text-brand-green">₹{order.totalAmount}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm font-body text-brand-green-dark">
                <div>
                  <p className="text-xs uppercase tracking-wider text-brand-gold mb-1">Customer</p>
                  <p>{order.customerName}</p>
                  <p className="text-brand-green-dark/70">{order.phone} · {order.email}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-brand-gold mb-1">Delivery</p>
                  <p className="text-brand-green-dark/80">{order.address}, {order.city} {order.pincode}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs uppercase tracking-wider text-brand-gold mb-2">Items</p>
                <ul className="text-sm font-body text-brand-green-dark space-y-1">
                  {order.items?.map((item, idx) => {
                    const product = productsMap[item.productId] || {};
                    return (
                      <li key={`${item.productId}-${item.orderType}-${idx}`}>
                        <span className="font-semibold">{product.name || item.productId}</span>
                        <span className="text-brand-green-dark/70"> · {getOrderTypeLabel(item.orderType, item.quantity)} · ₹{item.price * item.quantity}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-brand-gold mb-2">Status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map(s => (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(order.id, s)}
                      className={`px-3 py-2 rounded-sm text-xs font-body uppercase tracking-wider smooth-transition ${
                        order.status === s
                          ? 'bg-brand-green text-brand-cream'
                          : 'bg-brand-cream border border-brand-gold/40 text-brand-green-dark hover:border-brand-gold'
                      }`}
                      data-testid={`admin-status-${order.id}-${s.replace(/ /g, '-')}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
